import { Box, IconButton, Stack, Typography } from '@mui/material';
import React from 'react';
import { useDropzone } from 'react-dropzone';
import { Clear, AddAPhoto } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const DonationUploadFile = ({ value = [], onChange, error, sx = {} }) => {
  const { t } = useTranslation('translation');
  const { getRootProps, getInputProps, isDragReject, open } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (onChange) {
        onChange([...value, ...acceptedFiles]);
      }
    },
    onReject: (rejectedFiles) => {
      console.log({ rejectedFiles });
    },
    multiple: true,
    accept: 'image/jpeg, image/png, image/jpg',
    maxSize: 5242880, // 5MB
    noClick: true,
    noKeyboard: true,
  });

  const handleRemove = (idx) => {
    const newFiles = [...value];
    newFiles.splice(idx, 1);
    onChange(newFiles);
  };

  return (
    <Box>
      <Stack
        {...getRootProps()}
        sx={{
          border: (theme) =>
            `1px dashed ${
              isDragReject || error
                ? theme.palette.error.main
                : theme.palette.grey[300]
            }`,
          borderRadius: 2,
          p: { xs: 2, sm: 4 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 220,
          backgroundColor: '#f7f9fa',
          transition: 'border-color 0.2s',
          position: 'relative',
          ...sx,
        }}
      >
        <input {...getInputProps()} />
        <Stack direction="row" alignItems="center" spacing={3} width="100%">
          <Box
            sx={{
              flexShrink: 0,
              border: (theme) =>
                `2px dashed ${
                  isDragReject || error
                    ? theme.palette.error.main
                    : theme.palette.secondary.main
                }`,
              borderRadius: '50%',
              p: 1,
            }}
          >
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                borderRadius: '50%',
                p: 5,
                background: '#fff',
                display: 'flex',
              }}
            >
              <AddAPhoto sx={{ fontSize: 40, color: 'primary.main' }} />
            </Stack>
          </Box>
          <Stack alignItems="start">
            <Typography variant="h6" fontWeight={700} color="text.primary">
              {t('donations.select-files')}
            </Typography>
            <Typography mt={1} variant="body2" color="text.secondary">
              {t('donations.drop-files-or')}{' '}
              <span
                style={{
                  color: '#1db954',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
                onClick={open}
                tabIndex={0}
                role="button"
              >
                {t('donations.click-here')}
              </span>{' '}
              {t('donations.to-browse-through-your-machine')}
            </Typography>

            {value && value.length > 0 && (
              <Stack direction="column" width="100%" spacing={1} mt={2}>
                {value.map((file, idx) => {
                  const isUrl = typeof file === 'string';
                  return (
                    <Stack
                      key={isUrl ? idx : file.name}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={2}
                      sx={{
                        backgroundColor: '#fff',
                        border: (theme) =>
                          `1px solid ${theme.palette.grey[300]}`,
                        borderRadius: 1,
                        pl: 2,
                        pr: 1,
                        width: '100%',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {isUrl
                          ? t('donations.placeholder-image', {
                              index: idx + 1,
                            })
                          : file.name || file.path}
                      </Typography>
                      <IconButton onClick={() => handleRemove(idx)}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
      {error && (
        <Typography variant="caption" color="error" display="block" mt={1}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default DonationUploadFile;
